
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Calendar, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { utcToLocalDateTime } from "@/utils/timezoneUtils";
import ConsumerHeader from "@/components/ConsumerHeader";
import CacheManager from "@/components/CacheManager";
import MobileAppLayout from "@/components/app/MobileAppLayout";
import { useAppMode } from "@/hooks/useAppMode";

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAppMode, provider: appProvider } = useAppMode();
  const brandPrimary = appProvider?.brand_primary_color || undefined;
  const { toast } = useToast();
  const [verifying, setVerifying] = useState(true);
  const [verificationCompleted, setVerificationCompleted] = useState(false);
  const [booking, setBooking] = useState<any>(null);

  // Enhanced session recovery state
  const [recoveryAttempts, setRecoveryAttempts] = useState(0);
  const [sessionRecoveryId, setSessionRecoveryId] = useState<string | null>(null);

  // Enhanced webhook trigger function for payment errors with recovery data
  const triggerErrorWebhook = async (errorData: any) => {
    try {
      const sessionId = searchParams.get('session_id');
      const bookingId = searchParams.get('booking_id');
      
      const webhookPayload = {
        timestamp: new Date().toISOString(),
        error_type: 'payment_error_with_recovery',
        page: 'booking_success',
        url: window.location.href,
        user_agent: navigator.userAgent,
        session_id: sessionId,
        booking_id: bookingId,
        error_details: errorData,
        recovery_attempts: recoveryAttempts,
        session_recovery_id: sessionRecoveryId || `recovery_${Date.now()}`,
        // Include any user info if available
        user_info: null,
        booking_info: booking,
        recovery_data: {
          has_session_storage: !!sessionStorage.getItem(`verification_completed_${sessionId}` || `booking_completed_${bookingId}`),
          cached_booking: !!sessionStorage.getItem(`booking_data_${sessionId}` || `booking_data_${bookingId}`),
          verification_state: { verifying, verificationCompleted },
          timestamp: new Date().toISOString()
        }
      };

      // Get user info if available
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          webhookPayload.user_info = {
            id: user.id,
            email: user.email,
            created_at: user.created_at
          };
        }
      } catch (userError) {
        console.log('Could not get user info for webhook:', userError);
      }

      console.log('Triggering payment error webhook with payload:', webhookPayload);

      // Updated webhook URL to use the provided Pabbly Connect endpoint
      const webhookUrl = 'https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTZiMDYzMzA0MzA1MjZiNTUzMjUxMzAi_pc';
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(webhookPayload),
        mode: 'no-cors' // To handle CORS issues with external webhooks
      });
      console.log('Webhook triggered successfully');
    } catch (webhookError) {
      console.error('Failed to trigger error webhook:', webhookError);
    }
  };

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const bookingId = searchParams.get('booking_id');
    
    console.log('BookingSuccess: URL params:', { sessionId, bookingId });
    console.log('BookingSuccess: Current verification state:', { verifying, verificationCompleted });

    // Check if verification was already completed for this session
    const completionKey = sessionId ? `verification_completed_${sessionId}` : `booking_completed_${bookingId}`;
    const wasAlreadyCompleted = sessionStorage.getItem(completionKey) === 'true';
    
    console.log('BookingSuccess: Checking completion status:', { completionKey, wasAlreadyCompleted });

    if (wasAlreadyCompleted) {
      console.log('BookingSuccess: Verification already completed, skipping');
      setVerificationCompleted(true);
      setVerifying(false);
      
      // Try to get cached booking data
      const cachedBookingKey = sessionId ? `booking_data_${sessionId}` : `booking_data_${bookingId}`;
      const cachedBookingData = sessionStorage.getItem(cachedBookingKey);
      if (cachedBookingData) {
        try {
          const parsedBooking = JSON.parse(cachedBookingData);
          console.log('BookingSuccess: Using cached booking data:', parsedBooking);
          setBooking(parsedBooking);
        } catch (error) {
          console.error('Error parsing cached booking data:', error);
        }
      }
      return;
    }

    if (sessionId && !verificationCompleted) {
      console.log('BookingSuccess: Starting payment verification');
      verifyPayment(sessionId);
    } else if (bookingId && !verificationCompleted) {
      console.log('BookingSuccess: Starting booking fetch');
      fetchBookingDetails(bookingId);
    } else if (!sessionId && !bookingId) {
      console.log('BookingSuccess: No session_id or booking_id found');
      setVerifying(false);
    }
  }, [searchParams, verificationCompleted]);

  const fetchBookingDetails = async (bookingId: string) => {
    try {
      console.log('Fetching booking details for ID:', bookingId);
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Current user:', user?.email);

      // Fetch booking with related provider and service information
      const { data: bookingData, error } = await supabase
        .from('bookings')
        .select(`
          *,
          providers:provider_id(name, address),
          services:service_id(name)
        `)
        .eq('id', bookingId)
        .single();

      console.log('Booking query result:', bookingData);
      console.log('Booking query error:', error);

      if (error) {
        console.error('Error fetching booking:', error);
        await triggerErrorWebhook({
          error_source: 'fetch_booking_details',
          error_message: error.message,
          error_code: error.code,
          booking_id: bookingId
        });
        throw error;
      }

      if (!bookingData) {
        const notFoundError = {
          message: "Booking not found",
          booking_id: bookingId
        };
        await triggerErrorWebhook({
          error_source: 'fetch_booking_details',
          error_message: 'Booking not found',
          booking_id: bookingId
        });
        throw new Error("Booking not found");
      }

      // FIXED: Use timezone utilities for consistent UTC to local time conversion
      const transformedBooking = {
        ...bookingData,
        start_time: convertUTCTimeToLocal(bookingData.booking_date, bookingData.start_time),
        end_time: convertUTCTimeToLocal(bookingData.booking_date, bookingData.end_time)
      };

      console.log('BookingSuccess: Setting complete booking data with local times:', transformedBooking);
      setBooking(transformedBooking);

      // Mark verification as completed and cache booking data
      const completionKey = `booking_completed_${bookingId}`;
      const cacheKey = `booking_data_${bookingId}`;
      sessionStorage.setItem(completionKey, 'true');
      sessionStorage.setItem(cacheKey, JSON.stringify(transformedBooking));
      setVerificationCompleted(true);

    } catch (error) {
      console.error('Booking fetch failed:', error);
      const errorData = {
        error_source: 'fetch_booking_details',
        error_message: error instanceof Error ? error.message : 'Unknown error',
        booking_id: bookingId
      };
      await triggerErrorWebhook(errorData);
      toast({
        title: "Error",
        description: "There was an issue fetching your booking details. Please contact support.",
        variant: "destructive"
      });
    } finally {
      setVerifying(false);
    }
  };

  // Session recovery mechanism with retry logic
  const attemptSessionRecovery = async (sessionId: string, attempt: number = 1) => {
    const maxRetries = 3;
    const retryDelay = attempt * 2000; // Exponential backoff: 2s, 4s, 6s
    
    console.log(`Session recovery attempt ${attempt} for session ${sessionId}`);
    
    try {
      // Wait before retry (except first attempt)
      if (attempt > 1) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
      
      const { data, error } = await supabase.functions.invoke('verify-payment', {
        body: { 
          sessionId,
          recoveryAttempt: attempt,
          sessionRecoveryId: sessionRecoveryId || `recovery_${Date.now()}`
        }
      });

      if (error && attempt < maxRetries) {
        console.log(`Recovery attempt ${attempt} failed, retrying...`);
        setRecoveryAttempts(attempt);
        return attemptSessionRecovery(sessionId, attempt + 1);
      }

      return { data, error };
    } catch (error) {
      if (attempt < maxRetries) {
        console.log(`Recovery attempt ${attempt} failed with exception, retrying...`);
        setRecoveryAttempts(attempt);
        return attemptSessionRecovery(sessionId, attempt + 1);
      }
      throw error;
    }
  };

  const verifyPayment = async (sessionId: string) => {
    try {
      console.log('Verifying payment with session ID:', sessionId);
      
      // Generate recovery ID for this session
      if (!sessionRecoveryId) {
        setSessionRecoveryId(`recovery_${sessionId}_${Date.now()}`);
      }
      
      const { data, error } = await attemptSessionRecovery(sessionId);

      console.log('Payment verification response:', { data, error });

      if (error) {
        console.error('Payment verification error:', error);
        await triggerErrorWebhook({
          error_source: 'verify_payment',
          error_message: error.message,
          session_id: sessionId,
          error_details: error,
          final_attempt: true,
          total_attempts: recoveryAttempts + 1
        });
        throw error;
      }

      if (data?.booking) {
        // FIXED: Use timezone utilities for consistent UTC to local time conversion
        const transformedBooking = {
          ...data.booking,
          start_time: convertUTCTimeToLocal(data.booking.booking_date, data.booking.start_time),
          end_time: convertUTCTimeToLocal(data.booking.booking_date, data.booking.end_time)
        };

        console.log('BookingSuccess: Setting booking data from payment verification with local times:', transformedBooking);
        setBooking(transformedBooking);

        // Mark verification as completed and cache booking data
        const completionKey = `verification_completed_${sessionId}`;
        const cacheKey = `booking_data_${sessionId}`;
        sessionStorage.setItem(completionKey, 'true');
        sessionStorage.setItem(cacheKey, JSON.stringify(transformedBooking));
        setVerificationCompleted(true);
      } else {
        const noDataError = {
          message: "No booking data received",
          session_id: sessionId
        };
        await triggerErrorWebhook({
          error_source: 'verify_payment',
          error_message: 'No booking data received',
          session_id: sessionId
        });
        throw new Error("No booking data received");
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
      const errorData = {
        error_source: 'verify_payment',
        error_message: error instanceof Error ? error.message : 'Unknown error',
        session_id: sessionId
      };
      await triggerErrorWebhook(errorData);
      toast({
        title: "Verification Error",
        description: "There was an issue verifying your payment. Please contact support if you were charged.",
        variant: "destructive"
      });
    } finally {
      setVerifying(false);
    }
  };

  // Helper function to convert UTC time to local time for display using timezone utilities
  const convertUTCTimeToLocal = (date: string, time: string): string => {
    try {
      // Create a full UTC ISO string for proper conversion
      const utcDateTime = `${date}T${time}Z`;
      const localTime = utcToLocalDateTime(utcDateTime);
      console.log('Time conversion:', {
        originalDate: date,
        originalTime: time,
        utcDateTime,
        localTime: localTime.localTimeDisplay
      });
      return localTime.localTimeDisplay;
    } catch (error) {
      console.error('Error converting time:', error);
      return time; // Return original if conversion fails
    }
  };

  const handleViewBookingDetails = () => {
    console.log('BookingSuccess: View Booking Details clicked');
    console.log('BookingSuccess: Current booking data:', booking);
    if (booking?.id) {
      const targetUrl = isAppMode ? `/app/booking/${booking.id}` : `/booking-details/${booking.id}`;
      console.log('BookingSuccess: Navigating to:', targetUrl);
      navigate(targetUrl);
    } else {
      navigate(isAppMode ? "/app" : "/dashboard");
    }
  };

  // App-mode rendering — uses MobileAppLayout for native app styling
  if (isAppMode) {
    if (verifying) {
      return (
        <MobileAppLayout title="Booking">
          <div className="px-4 py-16 text-center">
            <div
              className="animate-spin rounded-full h-10 w-10 border-2 border-muted border-t-foreground mx-auto mb-4"
              style={brandPrimary ? { borderTopColor: brandPrimary } : undefined}
            />
            <h2 className="text-lg font-semibold mb-1">Verifying payment</h2>
            <p className="text-sm text-muted-foreground">Please wait while we confirm your booking…</p>
          </div>
        </MobileAppLayout>
      );
    }

    return (
      <MobileAppLayout title="Booking confirmed">
        <CacheManager forceCheck={false} showNotification={false} />
        <div className="px-4 py-6 space-y-6">
          <div className="flex flex-col items-center text-center pt-4">
            <div
              className="h-16 w-16 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: brandPrimary ? `${brandPrimary}20` : "hsl(var(--muted))" }}
            >
              <CheckCircle
                className="h-9 w-9"
                style={brandPrimary ? { color: brandPrimary } : { color: "hsl(var(--foreground))" }}
              />
            </div>
            <h1 className="text-2xl font-semibold mb-1">Booking confirmed</h1>
            <p className="text-sm text-muted-foreground max-w-xs">
              Your booking has been successfully confirmed
              {booking && Number(booking.price) > 0 ? " and your payment has been processed" : ""}.
            </p>
          </div>

          {booking && (
            <Card className="border-border">
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Booking number</span>
                  <span className="font-mono font-semibold">{booking.booking_number || booking.id}</span>
                </div>
                {booking.providers?.name && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Provider</span>
                    <span className="font-medium">{booking.providers.name}</span>
                  </div>
                )}
                {booking.booking_date && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Date</span>
                    <span className="font-medium">{booking.booking_date}</span>
                  </div>
                )}
                {booking.start_time && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Time</span>
                    <span className="font-medium">{booking.start_time}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <p className="text-xs text-muted-foreground text-center">
            You will receive a confirmation email shortly with all the details.
          </p>

          <div className="space-y-2">
            <Button
              onClick={handleViewBookingDetails}
              className="w-full h-12 text-base"
              style={brandPrimary ? { backgroundColor: brandPrimary, color: "#fff" } : undefined}
            >
              <Calendar className="h-4 w-4 mr-2" />
              View booking details
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/app/book")}
              className="w-full h-12 text-base"
            >
              Book another session
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </MobileAppLayout>
    );
  }


  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ConsumerHeader />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment</h2>
            <p className="text-gray-600">Please wait while we confirm your booking...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ConsumerHeader />
      {/* Don't force cache check on success page to avoid auth issues */}
      <CacheManager forceCheck={false} showNotification={false} />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-[34px]">
        <Card className="text-center">
          <CardHeader>
            <div className="mx-auto mb-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl text-green-600">Booking Confirmed!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-gray-600">
              Your booking has been successfully confirmed{booking && Number(booking.price) > 0 ? " and payment has been processed" : ""}.
            </p>

            {booking && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Booking Number:</span>
                  <span className="font-mono text-base font-semibold">{booking.booking_number || booking.id}</span>
                </div>
                {booking.providers?.name && (
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Provider:</span>
                    <span className="text-gray-900">{booking.providers.name}</span>
                  </div>
                )}
                {booking.booking_date && (
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Date:</span>
                    <span className="text-gray-900">{booking.booking_date}</span>
                  </div>
                )}
                {booking.start_time && (
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Time:</span>
                    <span className="text-gray-900">{booking.start_time}</span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                You will receive a confirmation email shortly with all the details.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={handleViewBookingDetails} className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4" />
                  <span>View Booking Details</span>
                </Button>
                
                <Button variant="outline" onClick={() => navigate("/")} className="flex items-center space-x-2">
                  <span>Book Another Service</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingSuccess;
